import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { ChickenAutoBetSettings, ChickenBetMode, ChickenDifficulty, ChickenGameState, ChickenRoundState } from '@/types/chicken'
import type { RootState } from '@/store/store'

export const initialChickenAutoBetSettings: ChickenAutoBetSettings = {
	steps: 1,
	betCount: 0,
	isInfinite: true,
	stopOnProfit: 0,
	stopOnLoss: 0,
}

export const initialChickenState: ChickenGameState = {
	phase: 'ready',
	betMode: 'manual',
	chickenGameId: null,
	currencyCode: 'USD',
	betAmount: '1',
	difficulty: 'easy',
	step: 0,
	multiplierSteps: [],
	autoBetSettings: initialChickenAutoBetSettings,
	autoBetRunning: false,
	isSubmitting: false,
	isAnimating: false,
	isResultVisible: false,
	currentMultiplier: 0,
	currentPayout: 0,
	currentResult: { multiplier: 0, amount: 0, payout: 0, profit: 0 },
	winAmount: 0,
}

function clampAutoSteps(steps: number, totalRoads: number) {
	const max = Math.max(1, totalRoads)
	return Math.min(max, Math.max(1, Math.floor(steps) || 1))
}

const chickenSlice = createSlice({
	name: 'chicken',
	initialState: initialChickenState,
	reducers: {
		resetChickenGame(state) {
			return {
				...initialChickenState,
				currencyCode: state.currencyCode,
				betAmount: state.betAmount,
				difficulty: state.difficulty,
				multiplierSteps: state.multiplierSteps,
				betMode: state.betMode,
				autoBetSettings: {
					...state.autoBetSettings,
					steps: clampAutoSteps(state.autoBetSettings.steps, state.multiplierSteps.length),
				},
			}
		},
		setChickenCurrencyCode(state, action: PayloadAction<string>) {
			state.currencyCode = action.payload
		},
		setChickenBetMode(state, action: PayloadAction<ChickenBetMode>) {
			if (state.phase === 'live' || state.autoBetRunning) {
				return
			}

			state.betMode = action.payload
		},
		setChickenBetAmount(state, action: PayloadAction<string>) {
			state.betAmount = action.payload
		},
		setChickenDifficulty(state, action: PayloadAction<ChickenDifficulty>) {
			if (state.phase === 'live' || state.autoBetRunning) {
				return
			}

			state.difficulty = action.payload
		},
		setChickenMultiplierSteps(state, action: PayloadAction<number[]>) {
			state.multiplierSteps = action.payload
			state.autoBetSettings.steps = clampAutoSteps(state.autoBetSettings.steps, action.payload.length)
		},
		setChickenAutoBetSettings(state, action: PayloadAction<Partial<ChickenAutoBetSettings>>) {
			state.autoBetSettings = {
				...state.autoBetSettings,
				...action.payload,
				steps: clampAutoSteps(action.payload.steps ?? state.autoBetSettings.steps, state.multiplierSteps.length),
			}
		},
		setChickenAutoBetRunning(state, action: PayloadAction<boolean>) {
			state.autoBetRunning = action.payload
		},
		setChickenSubmitting(state, action: PayloadAction<boolean>) {
			state.isSubmitting = action.payload
		},
		setChickenAnimating(state, action: PayloadAction<boolean>) {
			state.isAnimating = action.payload
		},
		applyChickenRoundState(state, action: PayloadAction<ChickenRoundState>) {
			const data = action.payload
			state.step = data.step
			state.currentMultiplier = data.multiplier
			state.currentPayout = data.payout
			state.isSubmitting = false

			if (data.chickenGame) {
				state.chickenGameId = data.chickenGame.id
				state.currencyCode = data.chickenGame.currency_code
				state.difficulty = data.chickenGame.difficulty
				state.betAmount = String(data.chickenGame.amount)
			}

			if (data.status === 'bet') {
				state.phase = 'live'
				state.isResultVisible = false
				return
			}

			state.phase = 'ready'
			state.chickenGameId = null
			state.isResultVisible = data.status === 'win' && !state.autoBetRunning

			if (data.result) {
				state.currentResult = data.result
				state.winAmount = data.result.profit
			}
		},
		dismissChickenResult(state) {
			state.isResultVisible = false
			state.step = 0
			state.currentMultiplier = 0
			state.currentPayout = 0
		},
	},
})

export const {
	resetChickenGame,
	setChickenCurrencyCode,
	setChickenBetMode,
	setChickenBetAmount,
	setChickenDifficulty,
	setChickenMultiplierSteps,
	setChickenAutoBetSettings,
	setChickenAutoBetRunning,
	setChickenSubmitting,
	setChickenAnimating,
	applyChickenRoundState,
	dismissChickenResult,
} = chickenSlice.actions

export const selectChickenGame = (state: RootState) => state.chicken

/** Preview/live lookup: step 1 → steps[0]. Returns 0 when missing. */
export function selectChickenStepMultiplier(steps: number[], step: number) {
	if (step <= 0) {
		return 0
	}

	return steps[step - 1] ?? 0
}

export default chickenSlice.reducer
